import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Info, RotateCcw, ChevronLeft, ChevronRight, X, Cpu, ChevronDown, Lock, Unlock } from 'lucide-react';
import { Badge } from '../ui/Badge';
// Types
interface ModelRun {
  modelId: string;
  timestamp: number;
}
interface ModelConfig {
  id: string;
  name: string;
  color: string;
  badge?: string;
  badgeColor?: 'pink' | 'blue' | 'default';
  availability: number;
}
const MODELS: ModelConfig[] = [{
  id: 'ept-2',
  name: 'EPT-2',
  color: '#EC4899',
  availability: 1.0
}, {
  id: 'ept-2-early',
  name: 'EPT-2 Early',
  color: '#d946ef',
  badge: 'Early',
  badgeColor: 'pink',
  availability: 0.9
}, {
  id: 'ept-2-hrrr',
  name: 'EPT-2 HRRR',
  color: '#f43f5e',
  badge: 'Hourly',
  badgeColor: 'pink',
  availability: 0.85
}, {
  id: 'ec-ifs',
  name: 'EC-IFS',
  color: '#06b6d4',
  availability: 1.0
}, {
  id: 'gfs',
  name: 'GFS',
  color: '#f59e0b',
  availability: 1.0
}, {
  id: 'ept-2e',
  name: 'EPT-2e',
  color: '#8b5cf6',
  availability: 0.8
}];
const RUN_HOURS = [0, 6, 12, 18];
// HRRR sub-hour offsets within each 6h block (6 bars per block, 1 per hour)
const HRRR_SUB_OFFSETS = [0, 1, 2, 3, 4, 5];
const isHourlyModel = (modelId: string) => modelId === 'ept-2-hrrr';
// Compute the latest available run for each model at init time
function getLatestRunsForAllModels(): ModelRun[] {
  const now = new Date();
  return MODELS.map(model => {
    let timestamp: number;
    if (isHourlyModel(model.id)) {
      const hourly = new Date(now);
      hourly.setUTCMinutes(0, 0, 0);
      timestamp = hourly.getTime();
    } else {
      const hour = Math.floor(now.getUTCHours() / 6) * 6;
      const sixHourly = new Date(now);
      sixHourly.setUTCHours(hour, 0, 0, 0);
      timestamp = sixHourly.getTime();
    }
    return {
      modelId: model.id,
      timestamp
    };
  });
}
export interface ModelOverride {
  modelNames: string[];
  runs: {
    modelId: string;
    modelName: string;
    timestamp: number;
  }[];
}
interface ModelRunSelectorProps {
  onOverrideChange?: (override: ModelOverride | null) => void;
}
export function ModelRunSelector({
  onOverrideChange
}: ModelRunSelectorProps) {
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [selectedRuns, setSelectedRuns] = useState<ModelRun[]>(getLatestRunsForAllModels);
  const [viewStartDate, setViewStartDate] = useState(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 2);
    d.setUTCHours(0, 0, 0, 0);
    return d;
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Generate 7 days
  const timelineDays = Array.from({
    length: 7
  }, (_, i) => {
    const d = new Date(viewStartDate);
    d.setUTCDate(d.getUTCDate() + i);
    return d;
  });
  // Click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const toggleRun = (modelId: string, timestamp: number) => {
    setSelectedRuns(prev => {
      const exists = prev.find(r => r.modelId === modelId && r.timestamp === timestamp);
      if (exists) return prev.filter(r => r !== exists);
      return [...prev, {
        modelId,
        timestamp
      }];
    });
  };
  const selectLatest = (modelId: string) => {
    const now = new Date();
    let timestamp: number;
    if (isHourlyModel(modelId)) {
      // For HRRR: round down to the current hour
      now.setUTCMinutes(0, 0, 0);
      timestamp = now.getTime();
    } else {
      // For standard models: round down to nearest 6h block
      const hour = Math.floor(now.getUTCHours() / 6) * 6;
      now.setUTCHours(hour, 0, 0, 0);
      timestamp = now.getTime();
    }
    setSelectedRuns(prev => {
      const withoutModel = prev.filter(r => r.modelId !== modelId);
      return [...withoutModel, {
        modelId,
        timestamp
      }];
    });
  };
  const clearModel = (modelId: string) => {
    setSelectedRuns(prev => prev.filter(r => r.modelId !== modelId));
  };
  const resetAll = () => {
    setSelectedRuns([]);
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  // Build override payload from current selections
  const buildOverride = (runs: ModelRun[]): ModelOverride => {
    const uniqueIds = [...new Set(runs.map(r => r.modelId))];
    const names = uniqueIds.map(id => MODELS.find(m => m.id === id)).filter(Boolean).map(m => m!.name);
    return {
      modelNames: names,
      runs: runs.map(r => {
        const model = MODELS.find(m => m.id === r.modelId);
        return {
          modelId: r.modelId,
          modelName: model?.name || r.modelId,
          timestamp: r.timestamp
        };
      })
    };
  };
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedRuns.length > 0) {
      onOverrideChange?.(buildOverride(selectedRuns));
    } else {
      onOverrideChange?.(null);
    }
  };
  // Notify parent when runs change while override is active
  useEffect(() => {
    if (overrideActive && selectedRuns.length > 0) {
      onOverrideChange?.(buildOverride(selectedRuns));
    } else if (overrideActive && selectedRuns.length === 0) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  }, [selectedRuns, overrideActive]);
  const scrollTimeline = (days: number) => {
    const d = new Date(viewStartDate);
    d.setUTCDate(d.getUTCDate() + days);
    setViewStartDate(d);
  };
  const jumpToToday = () => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 1);
    d.setUTCHours(0, 0, 0, 0);
    setViewStartDate(d);
  };
  const formatRunTime = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });
  };
  const getMonthLabel = () => {
    return timelineDays[3].toLocaleString('en-US', {
      month: 'long',
      timeZone: 'UTC'
    });
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getUTCDate() === today.getUTCDate() && date.getUTCMonth() === today.getUTCMonth() && date.getUTCFullYear() === today.getUTCFullYear();
  };
  // Summary for the trigger button
  const uniqueModels = [...new Set(selectedRuns.map(r => r.modelId))];
  const modelNames = uniqueModels.map(id => MODELS.find(m => m.id === id)?.name).filter(Boolean);
  const summaryText = selectedRuns.length === 0 ? 'No runs' : `${selectedRuns.length} run${selectedRuns.length !== 1 ? 's' : ''}`;
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      right: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right
    };
  };
  const dropdownStyle: React.CSSProperties = {
    background: 'rgba(11, 17, 32, 0.97)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  return <>
      {/* Trigger Button */}
      <button ref={triggerRef} onClick={() => setOpen(!open)} className={`jua-toolbar-btn flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${overrideActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : open ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-1681">
        {overrideActive ? <Lock size={12} className="text-amber-400" data-id="element-1682" /> : <Cpu size={12} className={open ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-1683" />}
        <span className="font-bold" data-id="element-1684">
          {overrideActive ? 'Override' : summaryText}
        </span>
        {modelNames.length > 0 && <>
            <span className={overrideActive ? 'text-amber-600' : 'text-slate-600'} data-id="element-1685">
              ·
            </span>
            <span className={`max-w-[120px] truncate ${overrideActive ? 'text-amber-500/70' : 'text-slate-500'}`} data-id="element-1686">
              {modelNames.join(', ')}
            </span>
          </>}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''} ${overrideActive ? 'text-amber-500' : open ? 'text-[#EC4899]' : 'text-slate-500'}`} data-id="element-1687" />
      </button>

      {/* Dropdown Panel */}
      {open && ReactDOM.createPortal(<div ref={panelRef} className="fixed rounded-xl shadow-2xl shadow-black/60 border border-white/[0.08] overflow-hidden z-[9999] flex flex-col" style={{
      ...dropdownStyle,
      top: getPanelPosition().top,
      right: getPanelPosition().right,
      width: 820,
      maxHeight: 620
    }} data-id="element-1688">
            {/* Inner glow */}
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
      }} data-id="element-1689" />

            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/[0.06]" data-id="element-1690">
              <div className="flex items-center gap-2" data-id="element-1691">
                <h3 className="text-sm font-semibold text-white" data-id="element-1692">Model Runs</h3>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.08] text-slate-400" data-id="element-1693">
                  Override all widgets when active
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1694">
                <button onClick={resetAll} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-1695">
                  <RotateCcw size={11} data-id="element-1696" /> Reset
                </button>
                <button onClick={() => setOpen(false)} className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-1697">
                  <X size={14} data-id="element-1698" />
                </button>
              </div>
            </div>

            {/* Selected Runs Pills */}
            <div className="relative px-4 py-2.5 border-b border-white/[0.06]" data-id="element-1699">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-1.5" data-id="element-1700">
                Included in analysis
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-1701">
                {selectedRuns.length === 0 && <span className="text-[11px] text-slate-600 italic" data-id="element-1702">
                    No runs selected — use the grid below
                  </span>}
                {selectedRuns.map(run => {
            const model = MODELS.find(m => m.id === run.modelId);
            if (!model) return null;
            return <div key={`${run.modelId}-${run.timestamp}`} className="flex items-center gap-1.5 pl-1.5 pr-0.5 py-0.5 rounded text-[11px] font-medium border transition-colors" style={{
              backgroundColor: `${model.color}15`,
              borderColor: `${model.color}30`
            }} data-id="element-1703">
                      <div className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: model.color
              }} data-id="element-1704" />
                      <span className="text-slate-200" data-id="element-1705">{model.name}</span>
                      <span className="text-slate-600" data-id="element-1706">·</span>
                      <span className="font-mono text-slate-400 text-[10px]" data-id="element-1707">
                        {formatRunTime(run.timestamp)}
                      </span>
                      <button onClick={() => toggleRun(run.modelId, run.timestamp)} className="p-0.5 rounded hover:bg-white/[0.1] text-slate-500 hover:text-white transition-colors" data-id="element-1708">
                        <X size={10} data-id="element-1709" />
                      </button>
                    </div>;
          })}
              </div>
            </div>

            {/* Timeline Grid - 3 column layout */}
            <div className="relative flex flex-col flex-1 overflow-hidden" data-id="element-1710">
              {/* Fixed header row (nav + days) — all 3 columns in sync */}
              <div className="flex flex-shrink-0 border-b border-white/[0.06]" data-id="element-1711">
                {/* Left header */}
                <div className="w-36 flex-shrink-0 border-r border-white/[0.06]" data-id="element-1712">
                  <div className="h-9 border-b border-white/[0.06]" data-id="element-1713" />
                  <div className="h-[52px]" data-id="element-1714" />
                </div>
                {/* Middle header */}
                <div className="flex-1 overflow-x-hidden" data-id="element-1715">
                  {/* Nav bar */}
                  <div className="h-9 border-b border-white/[0.06] flex items-center justify-between px-3 min-w-max" data-id="element-1716">
                    <button onClick={() => scrollTimeline(-1)} className="p-0.5 hover:bg-white/[0.06] rounded text-slate-400 hover:text-white transition-colors" data-id="element-1717">
                      <ChevronLeft size={13} data-id="element-1718" />
                    </button>
                    <div className="flex items-center gap-5" data-id="element-1719">
                      <span className="text-[10px] font-medium text-slate-500" data-id="element-1720">
                        Time (UTC)
                      </span>
                      <span className="text-xs font-medium text-slate-400" data-id="element-1721">
                        {getMonthLabel()}
                      </span>
                      <button onClick={jumpToToday} className="text-[10px] font-medium text-[#EC4899] hover:text-[#db2777] transition-colors" data-id="element-1722">
                        Today
                      </button>
                    </div>
                    <button onClick={() => scrollTimeline(1)} className="p-0.5 hover:bg-white/[0.06] rounded text-slate-400 hover:text-white transition-colors" data-id="element-1723">
                      <ChevronRight size={13} data-id="element-1724" />
                    </button>
                  </div>
                  {/* Days header */}
                  <div className="flex min-w-max h-[52px]" data-id="element-1725">
                    {timelineDays.map(day => <div key={day.toISOString()} className={`w-28 flex-shrink-0 border-r border-white/[0.06] flex flex-col items-center justify-center py-1.5 ${isToday(day) ? 'bg-[#EC4899]/5' : ''}`} data-id="element-1726">
                        <div className={`text-base font-bold mb-0.5 ${isToday(day) ? 'text-[#EC4899]' : 'text-white'}`} data-id="element-1727">
                          {day.getUTCDate()}
                        </div>
                        <div className="flex w-full justify-between px-1.5" data-id="element-1728">
                          {RUN_HOURS.map(h => <div key={h} className="text-[9px] font-mono text-slate-600" data-id="element-1729">
                              {h.toString().padStart(2, '0')}
                            </div>)}
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Right header */}
                <div className="w-28 flex-shrink-0 border-l border-white/[0.06]" data-id="element-1730">
                  <div className="h-9 border-b border-white/[0.06]" data-id="element-1731" />
                  <div className="h-[52px]" data-id="element-1732" />
                </div>
              </div>

              {/* Scrollable model rows — all 3 columns scroll together vertically */}
              <div className="flex flex-1 overflow-y-auto overflow-x-hidden" data-id="element-1733">
                {/* LEFT - Model Names */}
                <div className="w-36 flex-shrink-0 border-r border-white/[0.06]" data-id="element-1734">
                  {MODELS.map(model => <div key={model.id} className="h-[44px] px-3 border-b border-white/[0.04] flex items-center gap-2.5" data-id="element-1735">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                backgroundColor: model.color
              }} data-id="element-1736" />
                      <div className="flex flex-col min-w-0" data-id="element-1737">
                        <span className="text-[11px] font-bold text-slate-200 truncate" data-id="element-1738">
                          {model.name}
                        </span>
                        {model.badge && <Badge variant={model.badgeColor || 'default'} className="w-fit px-1 py-0 text-[8px] h-3.5" data-id="element-1739">
                            {model.badge}
                          </Badge>}
                      </div>
                    </div>)}
                </div>

                {/* MIDDLE - Timeline rows */}
                <div className="flex-1 overflow-x-auto overflow-y-hidden" data-id="element-1740">
                  {MODELS.map(model => <div key={model.id} className="flex min-w-max h-[44px] border-b border-white/[0.04]" data-id="element-1741">
                      {timelineDays.map(day => <div key={day.toISOString()} className={`w-28 flex-shrink-0 border-r border-white/[0.06] flex items-center ${isToday(day) ? 'bg-[#EC4899]/5' : ''} ${isHourlyModel(model.id) ? 'gap-0 px-0.5' : 'justify-between px-2'}`} data-id="element-1742">
                          {isHourlyModel(model.id) ? RUN_HOURS.map(hour => <div key={hour} className="flex-1 flex items-center gap-[1px] px-[1px]" data-id="element-1743">
                                  {HRRR_SUB_OFFSETS.map((offset, oi) => {
                    const runTime = new Date(day);
                    const subHour = hour + offset;
                    runTime.setUTCHours(Math.floor(subHour), subHour % 1 * 60, 0, 0);
                    const timestamp = runTime.getTime();
                    const isSelected = selectedRuns.some(r => r.modelId === model.id && r.timestamp === timestamp);
                    const isFuture = timestamp > Date.now();
                    return <button key={oi} onClick={() => !isFuture && toggleRun(model.id, timestamp)} disabled={isFuture} className={`flex-1 h-[26px] rounded-[2px] transition-all ${isFuture ? 'bg-slate-800/20 cursor-not-allowed' : isSelected ? '' : 'bg-slate-700/30 hover:bg-slate-600/50 cursor-pointer'}`} style={{
                      backgroundColor: isSelected ? model.color : undefined
                    }} title={`${model.name} · ${formatRunTime(timestamp)}`} data-id="element-1744" />;
                  })}
                                </div>) : RUN_HOURS.map(hour => {
                  const runTime = new Date(day);
                  runTime.setUTCHours(hour, 0, 0, 0);
                  const timestamp = runTime.getTime();
                  const isSelected = selectedRuns.some(r => r.modelId === model.id && r.timestamp === timestamp);
                  const isFuture = timestamp > Date.now();
                  return <button key={hour} onClick={() => !isFuture && toggleRun(model.id, timestamp)} disabled={isFuture} className={`w-[18px] h-[18px] rounded-[3px] border transition-all flex items-center justify-center ${isSelected ? 'border-transparent' : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'} ${isFuture ? 'opacity-20 cursor-not-allowed border-slate-800' : 'cursor-pointer'}`} style={{
                    backgroundColor: isSelected ? model.color : undefined
                  }} title={`${model.name} · ${formatRunTime(timestamp)}`} data-id="element-1745">
                                    {isSelected && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-1746">
                                        <polyline points="20 6 9 17 4 12" data-id="element-1747" />
                                      </svg>}
                                  </button>;
                })}
                        </div>)}
                    </div>)}
                </div>

                {/* RIGHT - Actions */}
                <div className="w-28 flex-shrink-0 border-l border-white/[0.06] shadow-[-8px_0_16px_-4px_rgba(0,0,0,0.5)]" data-id="element-1748">
                  {MODELS.map(model => <div key={model.id} className="h-[44px] px-2 border-b border-white/[0.04] flex items-center gap-1.5" data-id="element-1749">
                      <button onClick={() => selectLatest(model.id)} className="px-1.5 py-0.5 rounded bg-white/[0.06] hover:bg-white/[0.1] text-[9px] font-medium text-slate-300 hover:text-white transition-colors border border-white/[0.04]" data-id="element-1750">
                        Latest
                      </button>
                      <button onClick={() => clearModel(model.id)} className="px-1.5 py-0.5 rounded hover:bg-red-500/10 text-[9px] font-medium text-slate-500 hover:text-red-400 transition-colors" data-id="element-1751">
                        Clear
                      </button>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="relative px-4 py-2.5 flex items-center justify-between text-[11px] border-t border-white/[0.06]" data-id="element-1752">
              <div className="flex items-center gap-1.5" data-id="element-1753">
                <span className="font-bold text-[#EC4899]" data-id="element-1754">
                  {selectedRuns.length}
                </span>
                <span className="text-slate-400" data-id="element-1755">runs selected</span>
              </div>

              {/* Override Toggle */}
              <button onClick={toggleOverride} disabled={selectedRuns.length === 0} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${overrideActive ? 'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25' : selectedRuns.length === 0 ? 'bg-white/[0.02] border-white/[0.04] text-slate-600 cursor-not-allowed' : 'bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/[0.08]'}`} title={overrideActive ? 'Disable override — widgets revert to their own models' : 'Override all widgets with these model runs'} data-id="element-1756">
                {overrideActive ? <Lock size={10} data-id="element-1757" /> : <Unlock size={10} data-id="element-1758" />}
                {overrideActive ? 'Override Active' : 'Override All Widgets'}
              </button>
            </div>
          </div>, document.body)}
    </>;
}